import { Fragment, useContext } from 'react';

import { faDroplet } from '@awesome.me/kit-f3dc60f0d0/icons/classic/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TableCell } from '@repo/videa-components/src/ui/table';
import classNames from 'classnames';

import { isReverseOrderDcm } from './helpers';
import {
  PerioChartModeContext,
  type ToothSurface,
  type ToothSurfaceMeasurement,
  type ToothSurfaces,
} from './types';

const TOOTH_SURFACES_IN_ORDER: ToothSurface[] = [
  'distal',
  'center',
  'mesial',
] as const;

const TOOTH_SURFACES_IN_REVERSE_ORDER: ToothSurface[] = [
  'mesial',
  'center',
  'distal',
] as const;

/**
 * Probing Depth (mm) - positive value; -1 when not present
 * Clinical Attachment Level (mm) - zero or positive value; -1 when not present
 * Bleeding (boolean) - true when bleeding, false when not bleeding
 * Suppuration (boolean) - true when suppuration, false when not suppuration
 * Gingival Margin (mm) - 0-9 values; -1 when not present
 * Mucogingival Junction (boolean) - positive value; -1 when not present
 * Furcation Grade (integer) - 0-4 values; -2 when not present
 */

const renderNumberMeasurement = (
  type:
    | 'probingDepth'
    | 'gingivalMargin'
    | 'clinicalAttachmentLevel'
    | 'mucogingivalJunction'
    | 'furcationGrade',
  value: number | null,
  mode: 'light' | 'dark',
) => {
  const isWarningProbingDepth =
    type === 'probingDepth' && !!value && value >= 4;
  const isWarningClinicalAttachmentLevel =
    type === 'clinicalAttachmentLevel' && !!value && value >= 4;
  // For furcation grade, any positive value is a warning
  const isWarningFurcationGrade =
    type === 'furcationGrade' && !!value && value >= 1;

  // -1 is used to indicate that the value is not present (-2 for furcation
  // grade). We should also hide measurements of 0 to avoid cluttering the
  // chart.
  const shouldHideValue = !value || value === -1 || value === -2;
  const isWarning =
    isWarningProbingDepth ||
    isWarningClinicalAttachmentLevel ||
    isWarningFurcationGrade;

  return (
    <p
      className={classNames({
        'vai-text-white': mode === 'dark' && !isWarning,
        'vai-text-zinc-950': mode === 'light' && !isWarning,
        'vai-text-blaze-700 vai-font-bold': isWarning,
      })}
    >
      {shouldHideValue ? '' : value}
    </p>
  );
};

const renderBooleanMeasurement = (
  type: 'hasBleeding' | 'hasSuppuration',
  value: boolean | null,
) => {
  return value ? (
    <FontAwesomeIcon
      className={classNames({
        'vai-text-blaze-700': type === 'hasBleeding',
        'vai-text-orange-200': type === 'hasSuppuration',
      })}
      icon={faDroplet}
      size="xs"
    />
  ) : (
    <div />
  );
};

const renderMeasurementValue = (
  type: ToothSurfaceMeasurement,
  value: string | number | boolean | null,
  mode: 'light' | 'dark',
) => {
  switch (type) {
    case 'probingDepth':
    case 'clinicalAttachmentLevel':
    case 'gingivalMargin':
    case 'mucogingivalJunction':
    case 'furcationGrade':
      return renderNumberMeasurement(type, value as number | null, mode);
    case 'hasBleeding':
    case 'hasSuppuration':
      return renderBooleanMeasurement(type, value as boolean | null);
    default:
      return <div />;
  }
};

interface Props {
  toothNumber: string | null;
  toothSurfaces?: ToothSurfaces | null;
  type: ToothSurfaceMeasurement;
}

export const ToothSurfaceMeasurementCell = ({
  toothNumber,
  toothSurfaces,
  type,
}: Props) => {
  const mode = useContext(PerioChartModeContext);
  const surfacesOrder = isReverseOrderDcm(toothNumber)
    ? TOOTH_SURFACES_IN_REVERSE_ORDER
    : TOOTH_SURFACES_IN_ORDER;

  return (
    <TableCell className="vai-text-[12px] !vai-py-[2px] !vai-px-2 vai-border vai-border-zinc-700">
      <div className="vai-w-full vai-grid vai-grid-cols-3 vai-gap-1">
        {surfacesOrder.map((surface) => {
          const value = toothSurfaces?.[surface]?.[type] ?? null;

          return (
            <Fragment key={surface}>
              {renderMeasurementValue(type, value, mode)}
            </Fragment>
          );
        })}
      </div>
    </TableCell>
  );
};
